import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Geometry, Nullable } from '@tcc/common';
import { Location } from '../../../../types/Location';
import {
  getModalMetaData,
  getLocale,
  getCurrentSeasonCropUuid,
  getFieldRegionReportArdFeatureSelector,
  getArdDataFeatureSelector
} from '../../../../selectors';
import {
  getCurrentSeason,
  getCurrentSeasonCropCode,
} from '../../../../selectors/seasonSelectors';
import { isAirSeederPopulation } from '../../../../selectors/plantingSelectors';
import { REGION_NOTE } from '../../../../constants/modal';
import {
  requestDeleteActivity as deleteActivity,
  setSelectedActivity as setActivity
} from '../../../../actions/scoutingActions';
import { getInversedFieldBoundary } from '../../../../selectors/fieldSelectors';
import { getFieldUuid } from '../../../../selectors/fieldSelectorsNew';
import {
  closeModal,
  openModal,
} from '../../../../actions/modalActions';
import { trackEvent, trackHeapEvent } from '../../../../actions/analyticsActions';
import { useAppDispatch, useAppSelector } from '../../../../utils';
import { parseSeasonTag } from '../../../../utils/seasonUtils';
import {
  ModalMode,
  ModalSize,
  isArdFieldRegionReport,
} from '../../../../types';
import { currentFieldSelector } from '../../../../selectors/fieldsSelectors';
import { getGeoJson } from '../../../../utils/geo';
import { stableSha256FromValue } from '../../../../utils/hash';
import { getHarvestState, getLegacySeasonTag } from '../../../../selectors/harvestSelectors';
import { isValidHarvestData } from '../../../../utils/regionUtils';
import { logDmbError } from '../../../../utils/error';

import { 
  useRegionReportQueries,
} from './useReportQuery';
import {
  reportArdRegionReportSession,
} from './services/ardRegionReportTelemetry';
import {
  ArdRegionReportFailureStage,
  ArdRegionReportSessionOptions,
  ArdRegionReportSessionOutcome,
} from './services/types/telemetry';
import { getGraphQlErrorMessage } from './services/regionReportQueryService';
import { ArdRequestLifecycle, isLegacyQueryParams, MaybeQueryParams } from './types/queryParams';
import { RootLevelDetails } from './types/modals';

const modalStates = {
  list: 'list',
  detail: 'detail',
};

export enum FailReason {
  NoHarvestData = 'no_harvest_data',
  InvalidRegion = 'invalid_region',
  QueryError = 'query_error',
  EmptyReport = 'empty_report',
}

export type UseRegionReportControllerParams = {
  index: number;
};

export type SetItemCallback = (itemData: RootLevelDetails) => void;

export const useRegionReportController = ({
  index
}: UseRegionReportControllerParams) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((storeState) => storeState);
  const currentSeasonYear = getCurrentSeason(state, { index });
  const harvestData = getHarvestState(state, { index });
  const hasHarvestData = isValidHarvestData(harvestData);
  const { activity, mode: regionFormMode } = getModalMetaData(state);
  const fieldName = currentFieldSelector(state).name;
  const fieldBoundary = getInversedFieldBoundary(state);
  const fieldUUID = getFieldUuid(state);

  const seasonTag = currentSeasonYear;
  const isHarvestArdDataEnabled = useAppSelector(getArdDataFeatureSelector);
  const isFrrArdDataEnabled = useAppSelector(getFieldRegionReportArdFeatureSelector);
  const frrSeasonTag = isHarvestArdDataEnabled
    ? getLegacySeasonTag(state, { index })
    : seasonTag;

  const cropCode = getCurrentSeasonCropCode(state, { index });
  const isAirSeeder = isAirSeederPopulation(state, { index }) as boolean;
  const locale = getLocale(state);

  const [itemData, setItemData] = useState<Nullable<RootLevelDetails>>(null);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [closeConfirmationModal, setCloseConfirmationModal] = useState(false);
  const [child, setChild] = useState(modalStates.list);
  const geometry = (activity?.location || activity?.geometry) as Geometry | Location | undefined;
  const geometryHash = useMemo(() => stableSha256FromValue(geometry ?? {}), [geometry]);
  const isValidationEnabled = Boolean(fieldBoundary?.coordinates && geometry?.coordinates);
  const lastNullResponseTrackingRef = useRef('');
  const ardRequestLifecycleRef = useRef<ArdRequestLifecycle>({ pollAttemptCount: 0 });
  const sessionStartedAtRef = useRef(performance.now());
  const sessionActionReportedRef = useRef(false);
  const lastReportGraphQlErrorRef = useRef<string | undefined>(undefined);

  const activityExists = Boolean(activity?.id);

  let queryParams: MaybeQueryParams;
  if(isFrrArdDataEnabled) {
    const cropId = getCurrentSeasonCropUuid(state, {index});
    const seasonTagParsed = parseSeasonTag(frrSeasonTag);

    queryParams = {
      cropId,
      geometryHash,
      fieldUUID,
      geometry,
      seasonName: seasonTagParsed.seasonName,
      seasonYear: seasonTagParsed.year,
    };
  } else {
    queryParams = {
      geometry,
      geometryHash,
      fieldUUID,
      seasonTag: frrSeasonTag,
    }
  }

  const {
    isRegionValid,
    reportData,
    isLoadingReport,
    isReportSuccess,
    isReportFetched,
    reportError,
    hasReportError,
    reportDataUpdatedAt,
  } = useRegionReportQueries({
    activityExists,
    fieldBoundary,
    isValidationEnabled,
    hasHarvestData,
    ardRequestLifecycleRef,
    lastReportGraphQlErrorRef,
    queryParams
  });

  useEffect(() => {
    if (isReportSuccess && reportData) {
      dispatch(trackEvent('field_region_report', 'viewed', {
        shape: geometry?.type,
      }));
    }
  }, [reportDataUpdatedAt, isReportSuccess, reportData, geometry?.type, dispatch]);

  const hasRegionReportData = Boolean(
    reportData &&
    (isArdFieldRegionReport(reportData)
      ? reportData.stats.harvestedArea?.v
      : reportData.area?.v) &&
    isRegionValid
  );

  const trackArdFrrSession = useCallback((outcome: ArdRegionReportSessionOutcome, options: ArdRegionReportSessionOptions = {}) => {
    if (!isFrrArdDataEnabled || isLegacyQueryParams(queryParams) || sessionActionReportedRef.current) {
      return;
    }

    sessionActionReportedRef.current = true;
    reportArdRegionReportSession({
      activityExists,
      lifecycle: ardRequestLifecycleRef.current,
      outcome,
      options,
      queryParams,
      sessionStartedAt: sessionStartedAtRef.current,
    });
  }, [activityExists, isFrrArdDataEnabled, queryParams]);

  useEffect(() => {
    if (hasRegionReportData) {
      trackArdFrrSession(ArdRegionReportSessionOutcome.Success);
    }
  }, [hasRegionReportData, trackArdFrrSession]);

  useEffect(() => {
    if (isLoadingReport || hasRegionReportData) {
      return;
    }

    // Avoid firing telemetry before validation has resolved.
    if (hasHarvestData && isValidationEnabled && isRegionValid === null) {
      return;
    }

    const hasNullResponseGraphQlError = Boolean(
      isReportSuccess
      && isReportFetched
      && !reportData
      && lastReportGraphQlErrorRef.current
    );

    let reason = FailReason.EmptyReport;
    if (!hasHarvestData) {
      reason = FailReason.NoHarvestData;
    } else if (isRegionValid === false) {
      reason = FailReason.InvalidRegion;
    } else if (hasReportError || hasNullResponseGraphQlError) {
      reason = FailReason.QueryError;
    }

    if ((reason === FailReason.EmptyReport || reason === FailReason.QueryError) && !isReportFetched && hasHarvestData) {
      return;
    }

    const errorMessage = reason === FailReason.QueryError
      ? (getGraphQlErrorMessage(reportError)
        || reportError?.message
        || lastReportGraphQlErrorRef.current)
      : undefined;
    const trackingKey = `${fieldUUID}:${frrSeasonTag}:${geometryHash}:${reason}:${errorMessage || ''}`;

    if (lastNullResponseTrackingRef.current === trackingKey) {
      return;
    }

    lastNullResponseTrackingRef.current = trackingKey;

    if (reason === FailReason.NoHarvestData || reason === FailReason.InvalidRegion) {
      dispatch(trackEvent('FRR No Data', { reason }));
    }

    const noDataErrorMessage = errorMessage
      ? `[FRR] No Data (${reason}): ${errorMessage}`
      : `[FRR] No Data (${reason})`;

    logDmbError(new Error(noDataErrorMessage), {
      reason,
      requestError: errorMessage,
      fieldUUID,
      seasonTag: frrSeasonTag,
    });

    let failureStage: ArdRegionReportFailureStage;
    if(reason === FailReason.QueryError) {
      if (reportError?.message === 'Polling timed out') {
        failureStage = ArdRegionReportFailureStage.PollStatus;
      } else if (ardRequestLifecycleRef.current.reportFetchStartedAt) {
        failureStage = ArdRegionReportFailureStage.FetchReport;
      } else if (ardRequestLifecycleRef.current.requestId) {
        failureStage = ArdRegionReportFailureStage.PollStatus;
      } else {
        failureStage = ArdRegionReportFailureStage.Submit;
      }
    } else {
      failureStage = ArdRegionReportFailureStage.Render;
    }

    trackArdFrrSession(
      reason === FailReason.QueryError && reportError?.message === 'Polling timed out'
        ? ArdRegionReportSessionOutcome.Timeout
        : ArdRegionReportSessionOutcome.Failed,
      {
        error: errorMessage,
        errorType: reason,
        failureStage,
      }
    );
  }, [isLoadingReport, hasRegionReportData, hasHarvestData, isRegionValid, hasReportError, isReportSuccess, isReportFetched, fieldUUID, frrSeasonTag, geometryHash, isValidationEnabled, reportData, reportError, dispatch, trackArdFrrSession]);

  const onStep2Click = useCallback(() => {
    setItemData(null);
    setChild(modalStates.list);
  }, []);

  const onClickItem = useCallback((itemDataObj: Nullable<RootLevelDetails>) => {
    setItemData(itemDataObj);
    setChild(modalStates.detail);
  }, []);

  const onRegionButtonClick = useCallback(() => {
    if (!activityExists) {
      return;
    }

    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.EDIT_REGION,
      activity,
    }));
    dispatch(trackHeapEvent('View Field Region Note'));
  }, [index, activityExists, activity, dispatch]);

  const saveClick = useCallback(() => {
    dispatch(openModal(index, null as never, {
      type: REGION_NOTE,
      size: ModalSize.L,
      mode: ModalMode.ADD_REGION,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore It seems we are mixing activities and regions. But we are ignoring it for now give it might be a braking change.
      region: activity?.geometry,
    }));
  }, [index, activity?.geometry, dispatch]);

  const onNotePadClick = useCallback(() => {
    if (regionFormMode === ModalMode.ADD_REGION) {
      saveClick();
    } else {
      onRegionButtonClick();
    }
  }, [regionFormMode, saveClick, onRegionButtonClick]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationModal(false);
    dispatch(deleteActivity(activity, { index }));
  }, [activity, index, dispatch]);

  const onDeleteCancelConfirm = useCallback(() => {
    onDeleteClick();
  }, [onDeleteClick]);

  const onCancelConfirm = useCallback(() => {
    trackArdFrrSession(ArdRegionReportSessionOutcome.Canceled);
    setCloseConfirmationModal(false);
    dispatch(closeModal(index));
    dispatch(setActivity(index, null));
  }, [dispatch, index, trackArdFrrSession]);

  const onCancelClick = useCallback(() => {
    setCloseConfirmationModal(true);
  }, [setCloseConfirmationModal]);

  const onCancelGoBack = useCallback(() => {
    setCloseConfirmationModal(false);
  }, [setCloseConfirmationModal]);

  const onDeleteConfirm = useCallback(() => {
    setDeleteConfirmationModal(true);
  }, [setDeleteConfirmationModal]);

  const onDeleteCancelGoBack = useCallback(() => {
    setDeleteConfirmationModal(false);
  }, [setDeleteConfirmationModal]);

  const showSaveButton = regionFormMode === ModalMode.ADD_REGION;

  const geoJson = activity?.id
    ? getGeoJson([fieldBoundary, activity.location])
    : getGeoJson([fieldBoundary]);

  return {
    activity,
    activityExists,
    cropCode,
    fieldBoundary,
    fieldName,
    geoJson,
    hasRegionReportData,
    isAirSeeder,
    isDetailView: child === modalStates.detail,
    isListView: child === modalStates.list,
    isRegionValid,
    itemData,
    locale,
    onCancelClick,
    onCancelConfirm,
    onCancelGoBack,
    onDeleteCancelConfirm,
    onDeleteCancelGoBack,
    onDeleteConfirm,
    onClickItem,
    onNotePadClick,
    onRegionButtonClick,
    onStep2Click,
    reportData,
    saveClick,
    showSaveButton,
    isLoadingReport,
    showCloseConfirmationModal: closeConfirmationModal,
    showDeleteConfirmationModal: deleteConfirmationModal,
  };
};
